import { useEffect, useRef, useState } from 'react';

export type ThemedSelectOption<T extends string> = {
  value: T;
  label: string;
};

interface ThemedSelectProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ThemedSelectOption<T>[];
  className?: string;
  /** Width of the trigger button; defaults to auto/min-content. */
  buttonClassName?: string;
}

/**
 * Compact themed dropdown — a button + popover replacement for a native
 * <select>. Native <select> option lists render with the OS's own
 * styling (the blue highlight), which can't be themed via CSS; this
 * keeps the open list on-brand (#8e2a2f selected, cherry hover).
 *
 * Closes on outside click and on Escape. Matches the popover styling of
 * ServiceDropdown but in a smaller, inline form.
 */
export function ThemedSelect<T extends string>({
  value,
  onChange,
  options,
  className = '',
  buttonClassName = '',
}: ThemedSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center justify-between gap-2 font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] bg-white border rounded-[10px] px-4 py-2.5 cursor-pointer transition-colors focus:outline-none ${
          open
            ? 'border-[#8e2a2f]'
            : 'border-[#ede8e9] hover:border-[#d0c4c5]'
        } ${buttonClassName}`}
      >
        <span className="truncate">{selected?.label ?? ''}</span>
        <span
          className={`material-icons text-[18px] text-[#8e2a2f] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          expand_more
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-full bg-white border border-[#ede8e9] rounded-[12px] shadow-[0px_12px_28px_0px_rgba(58,51,53,0.12)] p-1.5 overflow-hidden whitespace-nowrap animate-in fade-in zoom-in duration-150"
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] cursor-pointer font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors ${
                  isSelected
                    ? 'bg-[#8e2a2f]/[0.08] text-[#8e2a2f]'
                    : 'text-[#3a3335] hover:bg-[#fcfaf7] hover:text-[#8e2a2f]'
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected ? (
                  <span className="material-icons text-[18px] text-[#8e2a2f]">
                    check
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
